import type { DemandeCreateDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { DossierDTO } from '@/features/api/extranet/dto/Dossier/Synthese'
import type { RechercheResultDemandeRemboursementDTO } from '@/features/api/extranet/dto/Demande/Recherche/Remboursements'
import { useDialog } from '@/features/interface/dialog'
import { useFavorisStore } from '@/features/interface/favoris/favorisStore'
import apiDemande from '@/features/services/demande/DemandeAllService'
import { loadRecherche } from '@/features/services/recherche'
import { navigateTo, type NavigateToDemande, type ToDispositif } from '@/router/navigateTo'
import { useAjouteDemande } from '../../demande/ajouter/useAjouteDemande'
import { API_PATH_DR_DEMANDES } from '../dr/useDRRechercheStore.ts'
import { compose } from '@/features/shared/tools/function.ts'
export function useRechercheDossierActions() {
  const { addFavori } = useFavorisStore()

  function displayDossier(code: string, openInNewTab: boolean = false) {
    return navigateTo({ to: 'detail', domaine: 'formations', module: 'dossier', code: code }, openInNewTab)
  }

  async function addDemande() {
    try {
      const storeAddDemande = useAjouteDemande()
      const demande = await storeAddDemande.showAjoute()

      navigateTo({
        to: 'detail',
        domaine: 'formations',
        module: 'demande',
        dispositif: demande.dispositif.toLowerCase() as ToDispositif,
        sousDispositif: 'sousDispositif' in demande ? demande.sousDispositif : null,
        id: demande.id,
      } as NavigateToDemande)
    } catch (err) {
      console.error(err)
    }
  }

  async function findBrouillonDR(codeDossier: string): Promise<RechercheResultDemandeRemboursementDTO | null> {
    const result = await loadRecherche<
      { search: string[]; statuts: string[] },
      RechercheResultDemandeRemboursementDTO
    >(
      API_PATH_DR_DEMANDES,
      {
        query: {
          search: [codeDossier],
          statuts: ['SaisieAdherent'],
        },
        orderBy: [],
        pagination: { pageNumber: 1, pageSize: 10 },
      },
      { sablier: false, dialogMode: 'retry' },
    )

    return result.data.find((dr) => dr.intitule === codeDossier) ?? null
  }

  async function addDemandeDR(item: DossierDTO) {
    const brouillonExistant = await findBrouillonDR(item.code!)

    if (brouillonExistant) {
      const dialog = useDialog()
      await dialog.showDialog({
        type: 'Ok',
        message: 'Une DR est déjà en cours pour ce dossier.',
      })
      navigateTo({
        to: 'detail',
        domaine: 'dr',
        module: 'demande',
        id: brouillonExistant.id,
      })
      return
    }

    const result = await apiDemande.addDemande({
      typeDemande: 'Remboursement',
      dispositif: item.dispositif,
      sousDispositif: item.sousDispositif,
      siret: item.etablissementAdherentPrincipal?.siret,
      typePublic: item.typePublic,
      consentementTraitementDonnees: true,
      codeDossier: item.code,
    } as DemandeCreateDTO)

    navigateTo({
      to: 'detail',
      domaine: 'dr',
      module: 'demande',
      id: result.id,
    })
  }

  function getStatusDR(item: DossierDTO) {
    const visible = ['Lecture', 'Ecriture'].includes(item.niveauPermissionDr ?? 'NC')
    const message = visible ? item.commentaireBlocagePermissionDr ?? undefined : undefined
    const disabled = item.niveauPermissionDr !== 'Ecriture'
    return {
      visible,
      message,
      disabled,
      addDemandeDR: compose(addDemandeDR, item)
    }
  }

  return { displayDossier, addFavori, addDemande, getStatusDR }
}
